import SummaryCard from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/MemberShipComponent/SummaryCard";
import { MEMBERSHIP_CARD_TYPE } from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/MemberShipComponent/membershipCardType";
import PlanCard from "./PlanCard";
import type { IMembershipPlansProps, MembershipPlan, Summary } from "./interface";

// An owned membership offered for renewal: Renew Now tag, best-match colouring.
const isRenewNowCard = (card: any) => !!(card?.is_renewal && card?.renew_now);

const mapPitchingCard = (card: any): MembershipPlan => ({
  id: card.membership_id,
  title: card.category,
  yearlyPrice: card.membership_discount_label
    ? (card.price_per_year_after_discount ?? card.price_per_year)
    : card.price_per_year,
  originalYearlyPrice: card.membership_discount_label ? card.price_per_year : undefined,
  badge: card.is_top_best_match
    ? "BEST_MATCH"
    : isRenewNowCard(card)
      ? "RENEW_NOW"
      : card.is_recommended
        ? "RECOMMENDED"
        : undefined,
  highlighted: !!card.is_best_match || isRenewNowCard(card),
  savings: card.savings?.membership_savings,
  cartSavings: card.savings?.savings_on_this_cart,
  benefitLines: card.pitch_points,
  discountLabel: card.membership_discount_label,
});

const mapUsageCard = (entry: any): MembershipPlan => ({
  id: entry.user_membership_id,
  title: entry.category,
  badge: entry.is_renew_now ? "RENEW_NOW" : entry.is_recommended ? "RECOMMENDED" : undefined,
  status: entry.status,
  expiryLabel: entry.expiring_text,
  expiry: entry.expiring_on_date,
  lifetimeSavings: entry.lifetime_savings,
  coupons: entry.exhaustible_coupons,
  couponUsages: entry.exhaustible_coupons_usages,
  benefits: entry.benefits,
  yearlyPrice: entry.renew_now
    ? entry.membership_discount_label
      ? (entry.price_per_year_after_discount ?? entry.price_per_year)
      : entry.price_per_year
    : undefined,
  originalYearlyPrice:
    entry.renew_now && entry.membership_discount_label && entry.price_per_year_after_discount != null
      ? entry.price_per_year
      : undefined,
  savings: entry.renew_now?.membership_savings,
  // Same CMS pitch points as the control center's renewal card; older payloads only carry benefit_lines.
  benefitLines: entry.renew_now?.pitch_points ?? entry.renew_now?.benefit_lines,
  renewHeading: entry.renew_now?.heading,
  discountLabel: entry.renew_now ? entry.membership_discount_label : undefined,
});

export const mapPitchingCards = (cards?: any[]): MembershipPlan[] => (cards ?? []).map(mapPitchingCard);

export const isUsageCard = (card: any) => card?.card_type === MEMBERSHIP_CARD_TYPE.USAGE;
export const isPitchCard = (card: any) => card?.card_type === MEMBERSHIP_CARD_TYPE.PITCH;

const MembershipPlans = ({ membershipSummary, cards = [], scrollRef }: IMembershipPlansProps) => {
  // Backend order: owned memberships, pitches, then inactive owned memberships.
  const plans = cards.map((card) => ({
    key: `${card.card_type}-${isUsageCard(card) ? card.user_membership_id : card.membership_id}`,
    plan: isUsageCard(card) ? mapUsageCard(card) : mapPitchingCard(card),
  }));
  const hasOwnedMemberships = cards.some(isUsageCard);

  const summary: Summary = {
    totalSavings: membershipSummary?.total_savings || 0,
    savedToday: membershipSummary?.saved_today || 0,
  };

  return (
    <div
      className="w-[544px] bg-black
        max-h-[calc(var(--mirror-frame-height,120vh)-340px)]
        overflow-y-auto pt-0 pb-[20px] rounded-br-[20px] hide-scrollbar
        [scrollbar-width:none]
        [-ms-overflow-style:none]
        [&::-webkit-scrollbar]:hidden
        rounded-bl-[20px] font-roboto self-start"
      ref={scrollRef}
      data-mirror-membership-scroll="true"
    >
      {hasOwnedMemberships ? (
        <SummaryCard data={summary} />
      ) : (
        <div
          className="w-full flex items-center gap-[10px] px-[32px] pt-[20px] pb-[50px] mb-[-30px]
            bg-gradient-to-br from-[#ffebd0] to-[#eabe83]
            border-b border-l border-r border-[#e09833] rounded-bl-[20px] rounded-br-[20px]"
        >
          <img src="/membership_crown.svg" alt="" className="w-[24px] h-[24px]" />
          <h2 className="text-[24px] font-medium text-black">Vetic Memberships</h2>
        </div>
      )}

      {plans.map(({ key, plan }, index) => (
        <div
          key={key}
          className={`flex justify-center ${
            index === 0 && hasOwnedMemberships ? "-mt-[30px] mb-[24px] relative z-10" : "mt-[24px] mb-[24px]"
          }`}
        >
          <PlanCard plan={plan} />
        </div>
      ))}
    </div>
  );
};

export default MembershipPlans;
